import { mapState, mapGetters, mapActions } from 'vuex';
import { get, set, isEqual, cloneDeep } from 'lodash';

import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT, SEARCH_PAGE_LIMIT } from '@/constants/pagination';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import actionItem from '../Partials/ActionItem/ActionItem.vue';
import buildingKitAiTesting from '../Partials/BuildingKitAiTesting/BuildingKitAiTesting.vue';
import MiddlePaneHeader from '../Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import ActionContainer from '../Partials/ActionContainer/ActionContainer.vue';
import aiqCommandBuilderDialog from '../Partials/BuildingKitAiTesting/BuildingKit/Commands/AIQCommandBuilder/AIQCommandBuilderDialog.vue';
import EditableInputRow from './EditableInputRow.vue';
import Priority from '@/pages/Settings/Agents/children/Customer/Segments/Priority.vue';
import { arePairsValid } from '@/libs/dialogHelpers';
import { EntityDialog } from '@/components';
import * as log from 'loglevel';
import IqToolsDiagram from '../Diagrams/IqToolsDiagram.vue';
import DiagramViewMixin from '../Diagrams/DiagramViewMixin';
import { getDialogDiagramInfo } from '../Diagrams/DiagramHelper';
import {
  IQ_TOOL_ACTION_TYPE_DIALOG,
  IQ_TOOL_ACTION_TYPE_WORKFLOW,
} from '@/constants/iqtool';

import {
  EntitySuggestionPopupsMixin,
  iqtoolsErrorHandler,
  makeDependencyAlertMsg,
  removeActionIfExist,
  removeDuplicateNameAction,
  confirmDiscardUnsavedChanges,
  Events,
} from '@/libs';
import { onSort } from '@/pages/Metrics/helpers/formatters';
import { dialogConstants } from '@/constants';

const {
  DIALOG_TYPES, DIALOG_TYPE_NAMES, MODELS, MODES,
} = dialogConstants;

export default {
  name: 'dialogs',
  mixins: [EntitySuggestionPopupsMixin, DiagramViewMixin],
  components: {
    actionItem,
    buildingKitAiTesting,
    MiddlePaneHeader,
    ActionContainer,
    ManagementPage,
    aiqCommandBuilderDialog,
    EditableInputRow,
    Priority,
    EntityDialog,
    IqToolsDiagram,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const dialogNameRegex = helpers.regex(/^[a-zA-Z_][a-zA-Z.0-9\s_]*$/);
    return {
      newDialogName: { required, dialogNameRegex },
    };
  },
  data() {
    return {
      search: '',
      searchPageSize: SEARCH_PAGE_LIMIT,
      loading: true,
      pagination: PAGINATION_DEFAULT,
      newDialogName: '',
      showAddDialog: false,
      formDialog: null,
      alternativePromptText: '',
      newButtonName: '',
      showNewButtonInput: true,
      buttonIndex: -1,
      // Diagram state
      nodes: [],
      edges: [],
      containers: [],
      commands: [],
      expandedNodeIds: [],
      diagramKey: 0,
      // Command editor state
      showEditCommandDialog: false,
      selectedCommand: {},
      kitLoaded: false,
      editingFlowNodeId: null,
    };
  },
  computed: {
    ...mapState({
      loaded: (state) => state.dialogs.dialogsLoaded,
      selectedDialog: (state) => state.dialogs.selected,
      conditionalDialogsActiveFlag: (state) => state.featureFlags.FEATURE_FLAGS.CONDITIONAL_DIALOGS,
    }),
    ...mapGetters({
      dialogs: 'dialogs/list',
      entitySelectionList: 'entities/selectionList',
      routingProps: 'routing/props',
    }),
    pristine() {
      return isEqual(this.formDialog, this.selectedDialog);
    },
    middleColumnsClassObj() {
      return { pristine: this.pristine };
    },
    DIALOG_TYPE_NAMES() {
      return DIALOG_TYPE_NAMES;
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
    isEditMode() {
      return !!(this.selectedDialog && this.formDialog);
    },
    isViewMode() {
      return !!(this.selectedDialog && !this.formDialog);
    },
    isListMode() {
      return !this.selectedDialog;
    },
    isChanged() {
      return !this.pristine && this.isEditMode;
    },
    showAlternativeAsk() {
      return get(this.formDialog, 'payload.text_type.alternative_ask');
    },
    showAlternatePromptBox() {
      const textType = get(this.formDialog, 'payload.text_type');
      return textType ? textType.confirmation && textType.always_ask : false;
    },
    dialogType() {
      return get(this.formDialog, 'payload.dialog_type', '');
    },
    dialogTypeName() {
      if (this.dialogType === DIALOG_TYPES.TEXT) {
        return DIALOG_TYPE_NAMES.TEXT;
      }
      if (this.dialogType === DIALOG_TYPES.BUTTON) {
        return DIALOG_TYPE_NAMES.BUTTON;
      }
      if (this.dialogType === DIALOG_TYPES.REST) {
        const subType = get(this.formDialog, 'payload.rest_type.mode', '');

        if (subType === MODES.ENTITY) {
          return DIALOG_TYPE_NAMES.ENTITY;
        }
        if (subType === MODES.BUTTON) {
          return DIALOG_TYPE_NAMES.BUTTON_GROUP; // TODO: This should be implemented
        }
        // No API-based subtype matched
        return null;
      }
      //TODO: should be implemented later
      // if (this.dialogType === DIALOG_TYPES.CONDITION) {
      //   return DIALOG_TYPE_NAMES.CONDITION;
      // }

      return null;
    },
    entityListForConditionalAction() {
      return get(this.formDialog, 'payload.rest_type.responsePairs', []).map((pair) => pair[0]);
    },
    usesConditionalActions() {
      const payload = get(this.formDialog, 'payload', {});
      return this.conditionalDialogsActiveFlag && payload[this.dialogType]?.use_conditional_actions;
    },
    fixedEntity() {
      return get(this.formDialog, 'payload.entity', '');
    },
    isFormValid() {
      const payload = get(this.formDialog, 'payload', {});
      const params = payload[this.dialogType];
      const fail = (msg) => ({ result: false, msg });
      const pass = { result: true };

      switch (this.dialogType) {
        case DIALOG_TYPES.TEXT:
          if (!payload.entity) {
            return fail('Please make sure an entity is specified.');
          }
          break;

        case DIALOG_TYPES.REST:
          if (!params.endpoint_name) {
            return fail('Please make sure an endpoint is specified.');
          }

          if (params.mode === MODES.ENTITY && !(arePairsValid(params.requestPairs) && arePairsValid(params.responsePairs))) {
            return fail('Please make sure request and responses are specified.');
          }
          break;

        default:
          return pass;
      }
      return pass;
    },
    showSuccessFailureActions() {
      return [DIALOG_TYPE_NAMES.TEXT, DIALOG_TYPE_NAMES.ENTITY].includes(this.dialogTypeName);
    },
    successActionTitle() {
      return this.usesConditionalActions
        ? this.$t('iq_tools_tab.dialogs.conditional_success_actions')
        : this.$t('iq_tools_tab.dialogs.success_actions');
    },
    useUniversalInputType() {
      return this.dialogType === DIALOG_TYPES.TEXT;
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isEditMode && this.isChanged) {
      const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
      return next(confirmedDiscard);
    }
    return next();
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch('entities/getEntitiesList'),
        this.$store.dispatch('dialogs/getEntityOperators'),
        this.$store.dispatch('settings/getAPISettings'),
      ]);
    } catch (err) {
      this.$aiq.notify.error(this.$t('iq_tools_tab.dialogs.unable_to_load'));
      log.error(err);
    }

    this._onSameRoute = this.handleReloadPage.bind(this);
    Events.on('navigate.same.route', this._onSameRoute);

    return this.onRouteChanged(this.$route);
  },
  beforeUnmount() {
    if (this._onSameRoute) {
      Events.off('navigate.same.route', this._onSameRoute);
    }
    return this.$store.dispatch('dialogs/resetDialogs');
  },
  watch: {
    $route(to) {
      this.onRouteChanged(to);
    },
    alternativePromptText(newValue) {
      set(this.formDialog, 'payload.text_type.confirmation_prompt', newValue);
    },
    'formDialog.payload.entity': {
      handler(entity, oldEntity) {
        if (entity && entity !== oldEntity) {
          this.insertEntitiesIntoTextTypeActions();
        }
      },
    },
    showAlternatePromptBox(newValue, oldValue) {
      this.clearReplacePromptSetting(newValue, oldValue);
    },
  },
  methods: {
    ...mapActions({
      getFunctions: 'buildingKit/getFunctions',
      getDBKAll: 'buildingKit/getDBKAll',
    }),

    onSortByName(a, b) {
      const aName = get(a, 'name', '').toLowerCase().replace(/\s+/g, '');
      const bName = get(b, 'name', '').toLowerCase().replace(/\s+/g, '');
      return onSort(aName, bName);
    },

    async loadList({ searchTerm, searchOffset }) {
      this.loading = true;
      const params = { q: searchTerm, limit: this.searchPageSize, offset: searchOffset };
      try {
        const response = await this.$store.dispatch('dialogs/getDialogsList', [params, params.offset === 0]);
        this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.showAddDialog = true;
    },

    closeAddDialog() {
      this.showAddDialog = false;
      this.newDialogName = '';
    },

    async addNewDialog() {
      if (!this.newDialogName) {
        return;
      }

      try {
        const response = await this.$store.dispatch('dialogs/createDialog', [{ name: this.newDialogName }, 'data']);
        this.$aiq.notify.success(this.$t('iq_tools_tab.dialogs.dialog_added'));
        this.newDialogName = '';
        this.closeAddDialog();

        const newId = get(response, 'data.id', null);
        if (newId) {
          return await this.editDialog(newId, true);
        }
      } catch (err) {
        iqtoolsErrorHandler(this, err, 'dialog');
      }
    },

    async handleReloadPage(pathInfo) {
      if (pathInfo.fullPath === this.$route.fullPath && this.selectedDialog) {
        return await this.goToListPage();
      }
    },

    async goToListPage() {
      if (this.isChanged) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }
      return await this.$store.dispatch('dialogs/resetSelected');
    },

    async onRouteChanged(to) {
      // First check if there's a stored dialog ID from routing action
      const storedDialogId = get(this.routingProps, 'dialogId', null);
      if (storedDialogId) {
        // Clear the stored ID after using it
        this.$store.dispatch('routing/setProps', { ...this.routingProps, dialogId: null });
        return await this.editDialog(storedDialogId, true);
      }

      // Fall back to hash-based navigation (backward compatibility)
      const dialogName = get(to, 'hash', '').replace('#', '').trim();
      if (dialogName) {
        return await this.editDialogByName(dialogName);
      }
    },

    async updateDialogInfoByFormDialog() {
      if (this.dialogType === DIALOG_TYPES.TEXT) {
        this.alternativePromptText = get(this.formDialog, 'payload.text_type.confirmation_prompt', '');
      } else if (this.dialogType === DIALOG_TYPES.BUTTON) {
        if (this.buttonIndex === -1 && get(this.formDialog, 'payload.list_type.response_values.length', 0) > 0) {
          this.buttonIndex = 0;
        }
      }

      if (this.dialogTypeName === null) {
        return await this.goToListPage();
      }
    },

    async editDialog(id, disableSaveWarning) {
      if (this.isChanged && this.selectedDialog && !disableSaveWarning) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }

      // Reset the button index when editing a dialog
      this.buttonIndex = -1;

      if (this.selectedDialog && this.selectedDialog.id === id) {
        this.formDialog = cloneDeep(this.selectedDialog);
        this.updateDialogInfoByFormDialog();
        return;
      }

      try {
        await this.$store.dispatch('dialogs/selectDialog', id);
        this.formDialog = cloneDeep(this.selectedDialog);
        this.updateDialogInfoByFormDialog();
      } catch {
        this.$aiq.notify.error(this.$t('iq_tools_tab.dialogs.not_found'));
        return await this.goToListPage();
      }
    },

    async editDialogByName(dialogName) {
      const response = await this.$store.dispatch('dialogs/getDialogsList', [
        { q: dialogName, offset: 0, limit: 1 },
        true,
      ]);
      const results = response.data?.data;
      if (response?.data?.pagination?.rowCount === 1 && results?.length > 0) {
        return await this.editDialog(results[0].id, true);
      }
    },

    onEditCancel() {
      this.formDialog = cloneDeep(this.selectedDialog);
      this.updateDialogInfoByFormDialog();
    },

    async onEditDialog() {
      return await this.editDialog(this.selectedDialog.id, true);
    },


    async viewDialog(id = null) {
      if (id === null && this.selectedDialog) {
        id = this.selectedDialog.id;
      }
      if (id === null) {
        return this.goToListPage();
      }

      if (this.isChanged) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }

      if (!this.selectedDialog || id !== this.selectedDialog.id) {
        this.nodes = [];
        this.edges = [];
        this.containers = [];
        this.expandedNodeIds = [];
        try {
          await this.$store.dispatch('dialogs/selectDialog', id);
        } catch {
          this.$aiq.notify.error(this.$t('iq_tools_tab.dialogs.not_found'));
          return await this.goToListPage();
        }
      }

      this.formDialog = null;
      if (this.selectedDialog) {
        const { nodes, edges, commands, containers } = getDialogDiagramInfo(this.selectedDialog);
        this.nodes = nodes;
        this.edges = edges;
        this.commands = commands;
        this.containers = containers || [];
        this.expandedNodeIds = [];
      }
    },

    async onCopyDialog() {
      try {
        const data = await this.$store.dispatch('dialogs/copyDialog', this.formDialog.id);
        this.$aiq.notify.success(this.$t('iq_tools_tab.dialogs.copied'));
        return await this.editDialog(data.id, true);
      } catch (err) {
        iqtoolsErrorHandler(this, err, 'dialog');
      }
    },

    async onDeleteDialog(id) {
      const dialog = this.dialogs.find((e) => e.id === id);
      if (!dialog) return;

      try {
        await this.$aiq.confirm(
          this.$t('iq_tools_tab.dialogs.delete.title'),
          this.$t('iq_tools_tab.dialogs.delete.confirm', { name: dialog.name }),
        );
        await this.$store.dispatch('dialogs/deleteDialog', id);
        this.$aiq.notify.success(this.$t('iq_tools_tab.dialogs.delete.dialog_deleted'));
        this.formDialog = null;
        return await this.goToListPage();
      } catch (e) {
        if (e !== 'cancel') {
          const { title, content } = makeDependencyAlertMsg('Dialog', e, { $t: this.$t });
          this.$aiq.confirm(title, content);
        }
      }
    },

    async onEditSave() {
      if (!this.isFormValid.result) {
        this.$aiq.notify.error(this.isFormValid.msg);
        return;
      }

      this.preparePayload();
      this.cleanUpConditionalActions();

      await this.$store.dispatch('dialogs/updateDialog', this.formDialog);
      this.$aiq.notify.success(this.$t('iq_tools_tab.dialogs.save.success'));
      return await this.editDialog(this.formDialog.id, true);
    },

    onDialogTypeChanged(value) {
      this.formDialog.payload = MODELS[value]
        ? cloneDeep(MODELS[value])
        : cloneDeep(MODELS[DIALOG_TYPE_NAMES.TEXT]);
    },

    preparePayload() {
      if (this.dialogType === DIALOG_TYPES.TEXT) {
        this.insertEntitiesIntoTextTypeActions();
      }
    },

    insertEntitiesIntoTextTypeActions() {
      const entity = get(this.formDialog, 'payload.entity', '');
      const conditionalActions = get(this.formDialog, 'payload.text_type.conditional_actions', []);
      conditionalActions.forEach(({ statements }) => {
        statements.forEach((statement) => { statement.operand_entity = entity });
      });
    },

    cleanUpConditionalActions() {
      if (!this.usesConditionalActions) return;
      const cActions = this.formDialog.payload[this.dialogType].conditional_actions || [];
      cActions.forEach((cActionsItem) => {
        cActionsItem.statements = cActionsItem.statements.filter(
          (s) => s.operator && s.value && s.input_type && s.operand_entity,
        );
      });
    },

    clearReplacePromptSetting(newValue, oldValue) {
      if (!this.formDialog.payload.text_type) return;
      if (!newValue && oldValue) {
        this.formDialog.payload.text_type.confirmation = false;
        this.alternativePromptText = '';
      } else {
        this.$nextTick(() => { this.$refs.spInputBox.focus() });
      }
    },

    addNewButtonToPayload() {
      if (!this.newButtonName) {
        return;
      }

      set(this.formDialog, 'payload.list_type.response_values',
        get(this.formDialog, 'payload.list_type.response_values', []));

      const buttons = get(this.formDialog, 'payload.list_type.response_values', []);
      if (buttons.some((b) => b.value === this.newButtonName)) {
        this.$aiq.notify.error(this.$t('iq_tools_tab.dialogs.button_name_already_exists'));
        return;
      }

      buttons.push({ value: this.newButtonName, actions: [] });
      if (this.buttonIndex === -1) this.buttonIndex = 0;
      this.newButtonName = '';
      this.showNewButtonInput = false;
    },

    onButtonIndexSelected(e) {
      this.buttonIndex = parseInt(e.currentTarget.getAttribute('index'), 10);
    },

    showButton(index) {
      return this.buttonIndex === index;
    },

    onRemoveButton(index) {
      const list = get(this.formDialog, 'payload.list_type.response_values', []);
      if (list.length === 1) {
        this.buttonIndex = -1;
      } else if (index <= this.buttonIndex && index !== 0) {
        this.buttonIndex -= 1;
      }
      this.formDialog.payload.list_type.response_values.splice(index, 1);
    },

    onButtonNameEdited(item) {
      this.formDialog.payload.list_type.response_values[item.index].value = item.content;
    },

    onButtonOrder(index, isUp) {
      const list = cloneDeep(get(this.formDialog, 'payload.list_type.response_values', []));
      const swapIndex = isUp ? index - 1 : index + 1;
      [list[index], list[swapIndex]] = [list[swapIndex], list[index]];
      set(this.formDialog, 'payload.list_type.response_values', list);
    },

    onDeleteFailureAction(idx) {
      removeActionIfExist(this.formDialog.payload[this.dialogType]?.failure_actions, idx);
    },

    onDeleteAction(idx) {
      const { actions } = this.formDialog.payload.list_type.response_values[this.buttonIndex];
      removeActionIfExist(actions, idx);
    },

    onAddActions(event, actions) {
      removeDuplicateNameAction(actions, event.newIndex, this.formDialog);
    },

    onAddFailureActions(event) {
      this.onAddActions(event, this.formDialog.payload[this.dialogType]?.failure_actions);
    },

    onAddListTypeActions(event) {
      this.onAddActions(event, this.formDialog.payload.list_type.response_values[this.buttonIndex].actions);
    },

    async onActionItemClick(item) {
      if (![IQ_TOOL_ACTION_TYPE_DIALOG, IQ_TOOL_ACTION_TYPE_WORKFLOW].includes(item.unit_type)) {
        return;
      }

      if (this.isChanged && this.selectedDialog && this.isEditMode) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }

      if (item.unit_type === IQ_TOOL_ACTION_TYPE_DIALOG) {
        return await this.editDialog(item.id, true);
      }
      return await this.$store.dispatch('routing/routeIqtoolActionItemById', item);
    },
  },
};

